import { useMemo } from "react";
import { color } from "../constants/color";
import calibriteLogoDark from "../assets/Calibrite-colour-circle_greybg.svg";
import calibriteLogoLight from "../assets/Calibrite-colour-circle_white.svg";

// Light theme: the signup card rendered white instead of dark, for the mailing
// list and unsubscribe pages. ?theme=light turns it on. Dark stays the default.
//
// Callable outside React because public/index.html runs the same check inline to
// set the .cb-theme-light class before first paint (it stops the dark body from
// flashing before React mounts). See the .cb-theme-light rules in
// src/styles/style.css and the token map in useThemeMode below.

export const isLightTheme = () => {
  try {
    return new URLSearchParams(window.location.search).get("theme") === "light";
  } catch {
    return false;
  }
};

// ?button_style controls the corner shape of the card and the buttons, on its
// own axis from the colour theme. "rounded" (the software default) or "square".
// Any other value falls back to rounded. This is independent of ?theme, so
// theme=light + button_style=square gives the flat design look, while
// theme=light on its own keeps rounded corners.
export const isSquareCorners = () => {
  try {
    const raw = (
      new URLSearchParams(window.location.search).get("button_style") || ""
    )
      .trim()
      .toLowerCase();
    if (raw === "square" || raw === "sharp") return true;
    if (raw === "" || raw === "rounded" || raw === "round") return false;
    console.warn(`Ignoring button_style="${raw}" — expected "rounded" or "square".`);
    return false;
  } catch {
    return false;
  }
};

// The tokens the card components read. Dark values equal today's, so the dark
// card does not change. Light values come from the Newsletter Signup design.
const DARK_TOKENS = {
  card: color.grey, // #404040
  text: color.lightWhite, // #E0E0E1
  secondaryText: color.lightWhite,
  cardBorder: "0.063rem solid white", // 1px white outline
  cardShadow: undefined, // Chakra default modal shadow
  logo: calibriteLogoDark,
  enBorder: "white",
};

const LIGHT_TOKENS = {
  card: "#ffffff",
  text: "#404040",
  secondaryText: "#595959",
  cardBorder: "1px solid #cccccc", // flat grey border, no outline
  cardShadow: "none",
  logo: calibriteLogoLight,
  enBorder: "#404040",
};

export const themeTokens = (light) => (light ? LIGHT_TOKENS : DARK_TOKENS);

export function useThemeMode() {
  return useMemo(() => isLightTheme(), []);
}

export function useThemeTokens() {
  return useMemo(() => themeTokens(isLightTheme()), []);
}

// Square corners for the card and buttons, from ?button_style. Independent of
// the colour theme.
export function useSquareCorners() {
  return useMemo(() => isSquareCorners(), []);
}

export default useThemeMode;
